
import React, { useState, useEffect } from 'react';
import { Card, CardContent } from "@/components/ui/card";
import { BadgeDollarSign, Users, MousePointerClick, TrendingUp } from "lucide-react";
import { useAuthSession } from '@/hooks/useAuthSession';
import { supabase } from '@/lib/supabase';
import { Skeleton } from "@/components/ui/skeleton";

interface AffiliateStatsProps {
  totalReferrals?: number;
  totalEarnings?: number;
  pendingEarnings?: number;
  clickCount?: number;
  conversionRate?: number;
}

export function AffiliateStats({ 
  totalReferrals: propTotalReferrals,
  totalEarnings: propTotalEarnings,
  pendingEarnings: propPendingEarnings,
  clickCount: propClickCount,
  conversionRate: propConversionRate
}: AffiliateStatsProps) {
  const { session } = useAuthSession();
  const [isLoading, setIsLoading] = useState(true);
  const [stats, setStats] = useState({
    totalReferrals: propTotalReferrals || 0,
    totalEarnings: propTotalEarnings || 0,
    pendingEarnings: propPendingEarnings || 0,
    clickCount: propClickCount || 0,
    conversionRate: propConversionRate || 0,
  });
  
  useEffect(() => {
    if (!session?.user?.id) return;

    const fetchAffiliateStats = async () => {
      setIsLoading(true);
      try {
        // First, get the affiliate ID
        const { data: affiliateData, error: affiliateError } = await supabase
          .from('affiliates')
          .select('id, referral_code')
          .eq('user_id', session.user.id)
          .single();
          
        if (affiliateError) throw affiliateError;
        
        if (affiliateData?.id) {
          // Get referrals count
          const { count: referralsCount, error: referralsError } = await supabase
            .from('affiliate_referrals')
            .select('*', { count: 'exact', head: true })
            .eq('affiliate_id', affiliateData.id);
            
          if (referralsError) throw referralsError;
          
          // Get paid commissions
          const { data: paidCommissions, error: paidError } = await supabase
            .from('affiliate_commissions')
            .select('amount')
            .eq('affiliate_id', affiliateData.id)
            .eq('status', 'paid');
            
          if (paidError) throw paidError;
          
          // Get pending commissions
          const { data: pendingCommissions, error: pendingError } = await supabase
            .from('affiliate_commissions')
            .select('amount')
            .eq('affiliate_id', affiliateData.id)
            .eq('status', 'pending');
            
          if (pendingError) throw pendingError;
          
          // Get clicks count
          const { count: clicksCount, error: clicksError } = await supabase
            .from('affiliate_clicks')
            .select('*', { count: 'exact', head: true })
            .eq('affiliate_id', affiliateData.id);
            
          if (clicksError) throw clicksError;
          
          // Calculate totals
          const totalPaid = paidCommissions?.reduce((sum, item) => sum + item.amount, 0) || 0;
          const totalPending = pendingCommissions?.reduce((sum, item) => sum + item.amount, 0) || 0;
          
          // Calculate conversion rate
          const conversionRate = clicksCount > 0 ? 
            Math.round((referralsCount / clicksCount) * 100) : 0;
          
          setStats({
            totalReferrals: referralsCount || 0,
            totalEarnings: totalPaid,
            pendingEarnings: totalPending,
            clickCount: clicksCount || 0,
            conversionRate,
          });
        }
      } catch (err) {
        console.error('Error fetching affiliate stats:', err);
      } finally {
        setIsLoading(false);
      }
    };

    fetchAffiliateStats();
  }, [session?.user?.id, propTotalReferrals, propTotalEarnings, propPendingEarnings, propClickCount, propConversionRate]);

  return (
    <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
      <Card>
        <CardContent className="p-6">
          <div className="flex items-start justify-between">
            <div>
              <p className="text-sm font-medium text-muted-foreground mb-1">Total Earnings</p>
              {isLoading ? (
                <Skeleton className="h-8 w-24" />
              ) : (
                <p className="text-2xl font-bold">₹{stats.totalEarnings.toLocaleString()}</p>
              )}
            </div>
            <div className="bg-green-100 p-2 rounded-full">
              <BadgeDollarSign className="h-5 w-5 text-green-600" />
            </div>
          </div>
          <p className="text-xs text-muted-foreground mt-2">
            <span className="text-green-500">▲</span> Lifetime earnings
          </p>
        </CardContent>
      </Card>
      
      <Card>
        <CardContent className="p-6">
          <div className="flex items-start justify-between">
            <div>
              <p className="text-sm font-medium text-muted-foreground mb-1">Pending Earnings</p>
              {isLoading ? (
                <Skeleton className="h-8 w-24" />
              ) : (
                <p className="text-2xl font-bold">₹{stats.pendingEarnings.toLocaleString()}</p>
              )}
            </div>
            <div className="bg-amber-100 p-2 rounded-full">
              <TrendingUp className="h-5 w-5 text-amber-600" />
            </div>
          </div>
          <p className="text-xs text-muted-foreground mt-2">
            <span className="text-amber-500">○</span> Will be paid next cycle
          </p>
        </CardContent>
      </Card>
      
      <Card>
        <CardContent className="p-6">
          <div className="flex items-start justify-between">
            <div>
              <p className="text-sm font-medium text-muted-foreground mb-1">Link Clicks</p>
              {isLoading ? (
                <Skeleton className="h-8 w-16" />
              ) : (
                <p className="text-2xl font-bold">{stats.clickCount.toLocaleString()}</p>
              )}
            </div>
            <div className="bg-blue-100 p-2 rounded-full">
              <MousePointerClick className="h-5 w-5 text-blue-600" />
            </div>
          </div>
          <p className="text-xs text-muted-foreground mt-2">
            <span className="text-blue-500">▲</span> Lifetime clicks
          </p>
        </CardContent>
      </Card>
      
      <Card>
        <CardContent className="p-6">
          <div className="flex items-start justify-between">
            <div>
              <p className="text-sm font-medium text-muted-foreground mb-1">Successful Referrals</p>
              {isLoading ? (
                <Skeleton className="h-8 w-16" />
              ) : (
                <p className="text-2xl font-bold">{stats.totalReferrals.toLocaleString()}</p>
              )}
            </div>
            <div className="bg-purple-100 p-2 rounded-full">
              <Users className="h-5 w-5 text-purple-600" />
            </div>
          </div>
          <p className="text-xs text-muted-foreground mt-2">
            <span className="text-purple-500">●</span> {stats.conversionRate}% conversion rate
          </p>
        </CardContent>
      </Card>
    </div>
  );
}
